import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { HttpError, api, isRetryable } from '../api';
import type { Blueprint, BlueprintPart, Health, ItemStatus, LearnStatus, Moment, MomentBlueprints, MomentItemStatus, MomentSeed, TranscriptLine } from '../types';
import { go } from '../router';
import { onSpeaking, playScene, speak, speakAll, chainedVoice, stopSpeaking } from '../tts';
import { isYou, lineFloorMs } from '../scene-lines';
import { turnState, turnSteps } from '../scene-turns';
import { normalise as normaliseKey } from '../a2ui/base';
import { currentLang, errorText, num, t } from '../i18n';
import { opensLine, patternNote } from '../opens';
import { roundRecovery } from '../learning-readiness';
import { iso, tPart, uiDir } from '../bidi-html';
import { questionOf, personOf } from '../newcomers';
import { answerLabel, nameHtml } from './newcomer-name';
import { loadOneButton, oneButtonOn } from '../one-button';
import { openAnswer } from './capture-open';
import { openReport } from './report-sheet';
import { makeClientId } from '../drafts';

/** The panel's resting height, as a share of the screen: the moment's district stays in view above it. */
export const REST = 0.58;

/** A rude or vulgar word's small tag, with how strong it is (v1 ticket 45); the item's own note says where it would offend. */
function rudeTag(rude: 'mild' | 'strong' | undefined) {
  return rude ? html`<span class="rude-tag" data-rude=${rude}>${t(`rude.${rude}`)}</span>` : nothing;
}

@customElement('moment-view')
export class MomentView extends LitElement {
  @property() momentId = '';
  @state() private moment?: Moment;
  @state() private error = '';
  @state() private revealed = new Set<number>();
  @state() private openVocab: number | null = null;
  @state() private allRevealed = false;
  @state() private speaking: string | null = null;
  @state() private items: MomentItemStatus[] = [];
  @state() private triageBusyKey = '';
  @state() private triageError = '';
  @state() private triageErrorKey = '';
  private pendingRestoreOperations = new Map<string, string>();
  /** The moment's seeds with the learner's state (v1 ticket 09); null until the items request answers. */
  @state() private seeds: MomentSeed[] | null = null;
  @state() private seedBusy = '';
  @state() private seedMsg = '';
  @state() private rTried = false;
  @state() private rUnderstood = false;
  @state() private rStuck = false;
  @state() private rNote = '';
  @state() private missionMsg = '';
  @state() private saving = false;
  @state() private beta = false;
  @state() private readiness?: LearnStatus;
  @state() private readinessChecked = false;
  @state() private readinessFailed = false;
  @state() private shareBusy = false;
  @state() private shareMsg = '';
  @state() private audioBusy = false;
  @state() private audioMsg = '';
  @state() private deleting = false;
  @state() private deleteMsg = '';
  /** The last delete failed in a way trying again can fix (connection, 5xx, 408, 429). */
  @state() private deleteRetry = false;
  private unsubSpeaking?: () => void;
  /** Whether `speaking` is heard yet, or its voice still on its way (its button prepares meanwhile). */
  @state() private voiceHeard = false;

  protected createRenderRoot() { return this; }

  async connectedCallback() {
    super.connectedCallback();
    this.unsubSpeaking = onSpeaking((now, heard) => {
      this.speaking = now;
      this.voiceHeard = heard;
    });
    window.addEventListener('keydown', this.onKey);
    document.documentElement.classList.add('moment-open');
    this.fresh = new Set((/^#new=([\d,]+)$/.exec(location.hash)?.[1] ?? '').split(',').filter(Boolean).map(Number));
    void this.loadBlueprints();
    // Independent fetches run together — halves serial latency on moment open.
    const [moment, items, health, readiness] = await Promise.all([
      api.getMoment(this.momentId),
      api.momentItems(this.momentId).catch(() => null),
      // Support sharing only exists in the invited beta; a personal instance
      // is already the owner's own, so the card stays off there.
      api.health().catch(() => null as Health | null),
      api.learnStatus().catch(() => null as LearnStatus | null),
    ]).catch((e: Error) => {
      this.error = errorText(e);
      return [undefined, null, null, null] as const;
    });
    this.beta = health?.beta === true;
    this.readiness = readiness ?? undefined;
    this.readinessChecked = true;
    this.readinessFailed = !readiness;
    if (moment) {
      this.moment = moment;
      const r = moment.missionReflection;
      if (r) {
        this.rTried = r.tried;
        this.rUnderstood = r.understood ?? false;
        this.rStuck = r.stuck ?? false;
        this.rNote = r.note ?? '';
      }
    }
    // Triage is progressive enhancement — the lesson still reads fine without it.
    if (items) this.items = items.items;
    if (items?.seeds) this.seeds = items.seeds;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubSpeaking?.();
    stopSpeaking();
    window.removeEventListener('keydown', this.onKey);
    document.documentElement.classList.remove('moment-open');
  }

  /** 🔊 button that reads `text` aloud in the target language; a transcript line's (`line`, its index) counts as heard. */
  private listen(text: string, cls = '', line?: number) {
    const on = this.speaking === text;
    const preparing = on && !this.voiceHeard;
    return html`<button
      class="listen ${cls} ${on ? 'on' : ''} ${preparing ? 'preparing' : ''}"
      aria-busy=${preparing ? 'true' : 'false'}
      title=${on ? t('moment.stopListen') : t('moment.listen')}
      aria-label=${on ? t('moment.stopListen') : t('moment.listen')}
      @click=${(e: Event) => {
        e.stopPropagation();
        if (!on && line !== undefined) this.heard({ line });
        void speak(this.moment!.id, text, { mock: this.moment?.mock, lang: this.moment?.targetLanguage });
      }}
    >${on ? '⏹' : '🔊'}</button>`;
  }

  /** The recording's lines are counted once a page view; the server counts a line once a day however often it plays. */
  private recordingHeard = false;

  /**
   * A line (or the recording) was heard (v1 ticket 29): its glue words count
   * toward knowing them. Progressive enhancement: a failure changes nothing on screen.
   */
  private heard(what: { line: number } | { recording: true }) {
    if ('recording' in what) {
      if (this.recordingHeard) return;
      this.recordingHeard = true;
    }
    void api.heard(this.momentId, what).catch(() => undefined);
  }

  /**
   * Target-language line: Latin transliteration big for learners who can't
   * read the script yet (Persian Finglish), script small alongside for
   * gradual exposure. Items without latin render as before.
   */
  private targetLine(x: { ca: string; latin?: string }) {
    return x.latin
      ? html`<span class="ca" dir="auto">${x.latin}</span> <span class="muted small" dir="auto">${x.ca}</span>`
      : html`<span class="ca" dir="auto">${x.ca}</span>`;
  }

  // -- blueprints (v1 ticket 08) --------------------------------------------
  // The transcript's lines are blueprints: the real sentences the learner's
  // words are asked in, never reviewed on their own. The server marks every
  // word, expression or reply the learner has in them, from any moment
  // (`GET /api/moments/:id/blueprints`). No box number ever shows here: a word
  // still in the fog is marked faintly (dashed), as the town draws fog, and a
  // word out of it solidly.

  @state() private blueprints: MomentBlueprints | null = null;
  /** Guards against a stale response (connectedCallback's own load, or an earlier triage-triggered one) overwriting a newer one. */
  private blueprintsSeq = 0;

  /** Progressive enhancement: until (or unless) the marks arrive, lines read as plain text. */
  private async loadBlueprints() {
    const seq = ++this.blueprintsSeq;
    let result: MomentBlueprints | null;
    try {
      result = await api.momentBlueprints(this.momentId);
    } catch {
      result = null;
    }
    if (seq === this.blueprintsSeq) this.blueprints = result;
  }

  // -- lines where the learner knows every word (v1 ticket 31) ----------------
  // The server names them (`known`, by ticket 29's one rule); the screen counts
  // them above the transcript and marks each quietly. An old recording brought
  // back by a round's end opens with `#new=…`, its newly known lines lit.

  @state() private whyKnown = false;
  @state() private chaining = false;
  private chainRun = 0;
  /** Lines the comeback that opened this screen named as new. */
  private fresh = new Set<number>();
  private freshShown = false;

  private knownLines(): number[] {
    const n = this.moment?.analysis.transcript.length ?? 0;
    return (this.blueprints?.known ?? []).filter((i) => i < n);
  }

  private renderKnown() {
    const known = this.knownLines();
    const total = this.moment?.analysis.transcript.length ?? 0;
    if (!known.length) return nothing;
    const count = total === 1 ? t('known.one') : known.length === total ? t('known.all', { total }) : t('known.count', { n: known.length, total });
    return html`<div class="known-lines">
      <button class="known-count" aria-expanded=${this.whyKnown} @click=${() => (this.whyKnown = !this.whyKnown)}>
        <span class="known-tick" aria-hidden="true">✓</span> ${count}
      </button>
      ${this.whyKnown ? html`<div class="muted small known-why">${t('known.why')}</div>` : nothing}
      ${known.length >= 2
        ? html`<button class="btn small known-listen ${this.chaining ? 'on' : ''}" @click=${() => this.listenKnown(known)}>
            ${this.chaining ? `⏹ ${t('moment.stopListen')}` : `🔊 ${t('known.listen')}`}
          </button>`
        : nothing}
    </div>`;
  }

  /** Plays the known lines back to back; each counts as heard (once a line a day on the server). Moves no box. */
  private async listenKnown(known: number[]) {
    if (this.chaining) { stopSpeaking(); return; }
    const m = this.moment!;
    const run = ++this.chainRun;
    this.chaining = true;
    try {
      await speakAll(m.id, known.map((i) => m.analysis.transcript[i].ca), { mock: m.mock, lang: m.targetLanguage }, (k) => this.heard({ line: known[k] }));
    } finally {
      if (run === this.chainRun) this.chaining = false;
    }
    // A glue word may have reached its tenth hearing: the count follows.
    void this.loadBlueprints();
  }

  // -- the imagined scene (a typed moment's lines around the learner's) -------

  @state() private sceneOn = false;
  /** The clip is being made or fetched (the first play can take a few seconds); Stop still works. */
  @state() private scenePreparing = false;
  private sceneRun = 0;

  /** Plays the whole scene in two voices (the lines one by one where it can't); each line counts as heard. */
  private async playTheScene() {
    if (this.sceneOn) { stopSpeaking(); return; }
    const m = this.moment!;
    const run = ++this.sceneRun;
    this.sceneOn = true;
    this.scenePreparing = true;
    const playing = () => { if (run === this.sceneRun) this.scenePreparing = false; };
    try {
      await playScene(m.id, m.analysis.transcript.map((l) => l.ca), { mock: m.mock, lang: m.targetLanguage, onPlaying: playing }, (k) => this.heard({ line: k }));
    } finally {
      if (run === this.sceneRun) {
        this.sceneOn = false;
        this.scenePreparing = false;
      }
    }
    void this.loadBlueprints();
  }

  // -- your turn: the scene as a role-play once the learner knows every word of their lines ----------------------
  // The other person's lines play through the listen route; at each of the learner's the role-play waits, showing
  // only its translation, until "I said it" or "Show me" reveals and plays it. Unscored: never an attempt, never a box.

  /** Where the role-play is: the step shown, and whether it plays, waits for the learner or has ended. Null when off. */
  @state() private turn: { at: number; phase: 'play' | 'wait' | 'done' } | null = null;
  private turnRun = 0;
  /** Every line of the role-play through one audio element, first played in a tap, so Safari keeps playing it. */
  private voice = chainedVoice();

  private turnRunning() {
    return !!this.turn && this.turn.phase !== 'done';
  }

  private startTurn() {
    if (this.turnRunning()) { this.stopTurn(); return; }
    this.turn = { at: 0, phase: 'play' };
    void this.runTurn(0, ++this.turnRun);
  }

  private stopTurn() {
    this.turnRun++;
    this.turn = null;
    stopSpeaking();
  }

  /**
   * Plays the steps from `from` on, stopping at the learner's next line (`said`: the one at `from` was just said,
   * so it plays). Anything else played or stopped meanwhile ends the role-play.
   */
  private async runTurn(from: number, run: number, said = false) {
    const m = this.moment!;
    const steps = turnSteps(m.analysis.transcript.map((l) => l.speaker));
    for (let k = from; k < steps.length; k++) {
      if (run !== this.turnRun) return;
      if (steps[k].you && !(said && k === from)) {
        this.turn = { at: k, phase: 'wait' };
        return;
      }
      this.turn = { at: k, phase: 'play' };
      this.heard({ line: steps[k].line });
      const text = m.analysis.transcript[steps[k].line].ca;
      // The line stays at least at reading pace, however soon its voice ends (no device voice, a failed clip).
      const floor = new Promise<void>((r) => setTimeout(r, lineFloorMs(text)));
      const played = await this.voice.say(m.id, text, { mock: m.mock, lang: m.targetLanguage });
      if (run !== this.turnRun) return;
      if (!played) { this.turn = null; return; }
      await floor;
      if (run !== this.turnRun) return;
    }
    this.turn = { at: steps.length - 1, phase: 'done' };
    // The lines heard count toward the glue words: the known lines follow.
    void this.loadBlueprints();
  }

  /** "I said it" or "Show me": the learner's line shows and plays, then the scene goes on. Called in the tap. */
  private saidIt() {
    if (this.turn?.phase !== 'wait') return;
    void this.runTurn(this.turn.at, this.turnRun, true);
  }

  /** The turn's gate, from the words the screen marks in each line (none until the blueprints have loaded). */
  private turnGate() {
    const lines = this.moment!.analysis.transcript.map((l, i) => {
      const bp = this.blueprintAt(i, l);
      return { speaker: l.speaker, parts: bp?.parts, latinParts: bp?.latinParts, patterns: bp?.patterns };
    });
    return turnState(lines, this.blueprints?.words ?? {});
  }

  /** Until every word marked in the learner's lines is out of the fog, one quiet line says what opens the turn. */
  private renderTurnLock() {
    const st = this.turnGate();
    if (st.open || !st.fog) return nothing;
    return html`<div class="muted small scene-turn-locked">
      <span>${st.fog === 1 ? t('moment.turnLockedOne') : t('moment.turnLocked', { n: st.fog })}</span>
      ${roundRecovery(this.readiness, this.readinessChecked, this.readinessFailed)}
    </div>`;
  }

  /** The role-play so far: each line reached with its translation; the learner's waiting one as its translation only. */
  private renderTurn() {
    const turn = this.turn;
    if (!turn) return nothing;
    const shown = this.moment!.analysis.transcript.slice(0, turn.at + 1);
    return html`<div class="scene-turn" aria-live="polite">
      ${shown.map((l, i) => {
        const you = isYou(l.speaker);
        if (you && i === turn.at && turn.phase === 'wait') {
          return html`<div class="scene-turn-line line-you scene-turn-wait">
            <div class="scene-turn-now">${t('moment.yourTurn')}</div>
            <div class="scene-turn-en" dir=${uiDir(l.en)}>${iso(l.en)}</div>
            <div class="scene-turn-acts">
              <button class="btn primary small scene-said" @click=${() => this.saidIt()}>${t('moment.turnSaid')}</button>
              <button class="btn ghost small scene-show" @click=${() => this.saidIt()}>${t('moment.turnShow')}</button>
            </div>
          </div>`;
        }
        return html`<div class="scene-turn-line ${you ? 'line-you' : ''} ${i === turn.at && turn.phase === 'play' ? 'on' : ''}">
          <span class="scene-who"><bdi dir="auto">${you ? t('moment.speakerYou') : l.speaker}</bdi></span>
          <div class="scene-turn-ca">${this.targetLine(l)}</div>
          <div class="muted small" dir=${uiDir(l.en)}>${iso(l.en)}</div>
        </div>`;
      })}
      ${turn.phase === 'done'
        ? html`<div class="scene-turn-end">
            <span class="scene-turn-all">${t('moment.turnAll')}</span>
            <button class="btn small scene-again" @click=${() => this.startTurn()}>${t('moment.turnAgain')}</button>
            <button class="btn ghost small scene-turn-close" @click=${() => this.stopTurn()}>${t('tend.close')}</button>
          </div>`
        : nothing}
    </div>`;
  }

  protected updated() {
    // The comeback's new lines, once they are drawn: bring the first into view.
    // The marks can arrive before the moment's lines are drawn.
    if (this.freshShown || !this.fresh.size || !this.blueprints) return;
    const line = this.querySelector('.line.known-new');
    if (!line) return;
    this.freshShown = true;
    line.scrollIntoView({ block: 'center' });
  }

  /** Line `i`'s blueprint, when it still matches the line on screen. */
  private blueprintAt(i: number, line: TranscriptLine): Blueprint | undefined {
    const bp = this.blueprints?.lines[i];
    return bp && bp.parts.map((p) => p.text).join('') === line.ca ? bp : undefined;
  }

  private markedParts(parts: BlueprintPart[]) {
    const words = this.blueprints?.words ?? {};
    return parts.map((p) => {
      if (!p.key) return p.text;
      const w = words[p.key];
      // Fog is told by the dashed line and the title, never by colour alone.
      return w?.fog
        ? html`<mark class="bp-word fog" title=${t('moment.fogMarkTitle', { meaning: w.en })}>${p.text}</mark>`
        : html`<mark class="bp-word" title=${w?.en ?? ''}>${p.text}</mark>`;
    });
  }

  /** A transcript line's target text as a blueprint, the learner's words marked (Finglish big, script small, like `targetLine`). */
  private blueprintLine(i: number, line: TranscriptLine) {
    const bp = this.blueprintAt(i, line);
    if (!bp) return this.targetLine(line);
    return line.latin && bp.latinParts
      ? html`<span class="ca" dir="auto">${this.markedParts(bp.latinParts)}</span> <span class="muted small" dir="auto">${this.markedParts(bp.parts)}</span>`
      : html`<span class="ca" dir="auto">${this.markedParts(bp.parts)}</span>`;
  }

  /** The meanings of the learner's words in line `i`, shown with its translation. */
  private blueprintMeanings(i: number, line: TranscriptLine, shown: boolean) {
    const bp = this.blueprintAt(i, line);
    const keys = [...new Set([...(bp?.parts ?? []), ...(bp?.latinParts ?? [])].map((p) => p.key).filter((k): k is string => !!k))];
    const words = keys.map((k) => this.blueprints?.words[k]).filter((w) => !!w);
    const patterns = bp?.patterns ?? [];
    if (!shown || (!words.length && !patterns.length)) return nothing;
    return html`<span class="bp-meanings">${words.map(
      (w) => html`<span class="bp-meaning"><bdi>${w!.latin ?? w!.ca}</bdi> — <bdi>${w!.en}</bdi></span>`,
    )}${patterns.map((p) => html`<span class="bp-meaning bp-pattern" data-pattern=${p.key}>${patternNote(p)}</span>`)}</span>`;
  }

  // -- triage -----------------------------------------------------------------

  private statusFor(ca: string): MomentItemStatus | undefined {
    return this.items.find((i) => i.key === normaliseKey(ca));
  }

  private setStatus = async (ca: string, status: ItemStatus) => {
    const key = normaliseKey(ca);
    if (!key || this.triageBusyKey === key) return;
    const current = this.items.find((item) => item.key === key);
    if (!current || current.status === status) return;
    this.triageBusyKey = key;
    this.triageError = '';
    this.triageErrorKey = '';
    const restoring = current.status === 'removed';
    const operationId = restoring
      ? this.pendingRestoreOperations.get(key) ?? makeClientId()
      : undefined;
    if (operationId) this.pendingRestoreOperations.set(key, operationId);
    try {
      const result = await api.setItemStatus(this.momentId, key, status, {
        expectedVersion: current.statusVersion,
        ...(operationId ? { operationId } : {}),
      });
      this.items = this.items.map((item) => item.key === key
        ? { ...item, status, statusVersion: result.statusVersion ?? current.statusVersion + 1 }
        : item);
      if (operationId) this.pendingRestoreOperations.delete(key);
      this.changed();
      // A restored word is marked again in the blueprints.
      if (restoring) void this.loadBlueprints();
    } catch (err) {
      if (err instanceof HttpError && err.status === 409) {
        this.pendingRestoreOperations.delete(key);
        this.triageError = t('lesson.triageConflict');
        this.triageErrorKey = key;
        const latest = await api.momentItems(this.momentId).catch(() => null);
        if (latest) {
          this.items = latest.items;
          if (latest.seeds) this.seeds = latest.seeds;
          void this.loadBlueprints();
        }
      } else {
        // Keep the prior status and, for restore, the same operation ID. If
        // the server committed before the response was lost, the next tap
        // replays that write and reconciles this view with the server.
        this.triageError = errorText(err);
        this.triageErrorKey = key;
      }
    } finally {
      this.triageBusyKey = '';
    }
  };

  /** What opens an expression the climb holds back (v1 ticket 29), in plain words; nothing when it is open. */
  private opensNote(ca: string) {
    const opens = this.statusFor(ca)?.opens;
    return opens ? html`<div class="muted small opens" style="margin-top:4px"><span class="opens-reason">${opensLine(opens)}</span>${roundRecovery(this.readiness, this.readinessChecked, this.readinessFailed)}</div>` : nothing;
  }

  private triageButtons(ca: string) {
    const st = this.statusFor(ca)?.status ?? 'active';
    const key = normaliseKey(ca);
    const busy = this.triageBusyKey === key;
    const triageError = this.triageErrorKey === key ? this.triageError : '';
    const btn = (s: ItemStatus, label: string, title: string) => html`<button
      class="btn ${st === s ? 'primary' : ''} small"
      style="min-height:28px;padding:2px 10px;text-transform:none"
      title=${title}
      ?disabled=${busy}
      @click=${(e: Event) => { e.stopPropagation(); void this.setStatus(ca, s); }}
    >${st === s ? '✓ ' : ''}${label}</button>`;
    if (st === 'removed') {
      return html`<div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px;align-items:center">
        <span class="muted small">${t('triage.isRemoved')}</span>
        ${btn('active', t('triage.bringBack'), t('triage.bringBackTitle'))}
        ${triageError ? html`<span class="small" role="alert">${triageError}</span>` : nothing}
      </div>`;
    }
    return html`<div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">
      ${btn('active', t('triage.active'), t('triage.activeTitle'))}
      ${btn('known', t('triage.known'), t('triage.knownTitle'))}
      ${btn('later', t('triage.later'), t('triage.laterTitle'))}
      ${triageError ? html`<span class="small" role="alert">${triageError}</span>` : nothing}
    </div>`;
  }

  private saveReflection = async () => {
    if (!this.rTried && !this.rUnderstood && !this.rStuck) {
      this.missionMsg = t('moment.markOne');
      return;
    }
    this.saving = true;
    this.missionMsg = '';
    try {
      const r = await api.saveMissionReflection(this.momentId, {
        tried: this.rTried,
        understood: this.rUnderstood,
        stuck: this.rStuck,
        note: this.rNote.trim() || undefined,
      });
      if (this.moment) this.moment = { ...this.moment, missionReflection: r.reflection };
      this.missionMsg = this.rStuck ? t('moment.savedStuck') : t('moment.savedNice');
    } catch (e) {
      this.missionMsg = errorText(e);
    } finally {
      this.saving = false;
    }
  };

  private toggleShare = async (on: boolean) => {
    this.shareBusy = true;
    this.shareMsg = '';
    try {
      if (on) {
        const { share } = await api.shareForSupport(this.momentId);
        if (this.moment) this.moment = { ...this.moment, sharedForSupport: { since: share.sharedAt } };
      } else {
        await api.endSupportShare(this.momentId);
        if (this.moment) this.moment = { ...this.moment, sharedForSupport: undefined };
      }
    } catch (e) {
      this.shareMsg = errorText(e);
    } finally {
      this.shareBusy = false;
    }
  };

  /**
   * Explicit, per-moment, reversible: the only way the owner sees a lesson.
   * The wording says exactly what it opens and for how long — no more.
   */
  private shareCard() {
    const shared = this.moment?.sharedForSupport;
    return html`<div class="card share-support">
      <h2>${t('share.title')}</h2>
      ${shared
        ? html`<div>${t('share.since', { date: fmtWhen(shared.since) })}</div>
            <div class="muted small" style="margin-top:6px">${t('share.sharedHint')}</div>
            <button class="btn block" style="margin-top:10px" ?disabled=${this.shareBusy}
              @click=${() => this.toggleShare(false)}>${t('share.stop')}</button>`
        : html`<div class="muted small">${t('share.offerHint')}</div>
            <button class="btn block" style="margin-top:10px" ?disabled=${this.shareBusy}
              @click=${() => this.toggleShare(true)}>${t('share.start')}</button>`}
      ${this.shareMsg ? html`<div class="muted small" style="margin-top:6px">${this.shareMsg}</div>` : nothing}
    </div>`;
  }

  /**
   * The original recording (ticket 13): the player while it exists — in the
   * beta with the day it goes by itself — and its own "Delete recording",
   * which keeps the lesson. Once it is gone, one line says when and why.
   */
  private recording(m: Moment) {
    if (m.audioFile) {
      return html`<div class="recording" style="margin-top:10px">
        <audio controls src=${api.audioUrl(m.id)} style="width:100%" @play=${() => this.heard({ recording: true })}></audio>
        ${this.beta && m.audioExpiresAt
          ? html`<div class="muted small recording-expiry" style="margin-top:4px">${t('audio.expiresOn', { date: fmtWhen(m.audioExpiresAt) })}</div>`
          : nothing}
        <button class="btn small recording-delete" style="margin-top:8px;min-height:30px;text-transform:none"
          ?disabled=${this.audioBusy} @click=${this.deleteRecording}>${t('audio.delete')}</button>
        ${this.audioMsg ? html`<div class="muted small" style="margin-top:6px">${this.audioMsg}</div>` : nothing}
      </div>`;
    }
    if (!m.audioRemoved) return nothing;
    const line = m.audioRemoved === 'expired'
      ? t('audio.expired', { date: fmtWhen(m.audioExpiresAt ?? m.audioRemovedAt ?? '') })
      : t('audio.deleted', { date: fmtWhen(m.audioRemovedAt ?? '') });
    return html`<div class="muted small recording-removed" style="margin-top:10px">${line}</div>`;
  }

  private deleteRecording = async () => {
    if (!this.moment || !confirm(t('audio.deleteConfirm'))) return;
    this.audioBusy = true;
    this.audioMsg = '';
    try {
      // The server's copy: no audio file any more, so the player goes with it.
      this.moment = await api.deleteAudio(this.moment.id);
    } catch (e) {
      this.audioMsg = errorText(e);
    } finally {
      this.audioBusy = false;
    }
  };

  private useStuckForNext = async () => {
    const note = this.rNote.trim() || this.moment?.mission || '';
    if (note) localStorage.setItem('pendingMissionNote', note);
    // With the lantern on, /record lands in the lantern, which never reads the note: the Type tab does.
    await loadOneButton();
    go(oneButtonOn() ? '/#type' : '/record');
  };

  // -- seeds (v1 ticket 09) ---------------------------------------------------
  // The moment's suggested replies are seeds: the learner keeps the ones they
  // want to be able to say, and only those are learned.

  /** Keep or un-keep a seed. The server's answer is the state shown; a retry is safe. */
  private toggleSeed = async (s: MomentSeed) => {
    if (this.seedBusy) return;
    this.seedBusy = s.key;
    this.seedMsg = '';
    try {
      const { seed } = await api.keepSeed(this.momentId, s.key, s.state !== 'kept');
      this.seeds = (this.seeds ?? []).map((x) => (x.key === seed.key ? { ...x, state: seed.state, opens: seed.opens } : x));
      this.changed();
    } catch (e) {
      this.seedMsg = errorText(e);
    } finally {
      this.seedBusy = '';
    }
  };

  private seedControl(s: MomentSeed) {
    if (s.state === 'word') return html`<span class="muted small seed-word">${t('moment.seedIsWord')}</span>`;
    const kept = s.state === 'kept';
    return html`<button class="btn ${kept ? 'primary' : ''} small seed-keep"
      style="min-height:30px;text-transform:none"
      aria-pressed=${kept ? 'true' : 'false'}
      title=${kept ? t('moment.unkeepSeedTitle') : t('moment.keepSeedTitle')}
      ?disabled=${this.seedBusy === s.key}
      @click=${() => this.toggleSeed(s)}>${kept ? t('moment.keptSeed') : t('moment.keepSeed')}</button>`;
  }

  // -- the panel over the town --------------------------------------------------
  // Arrival prototype D's split layout: the title and the lines scroll inside the panel, while the suggested replies
  // (with Keep) and the main button stay in reach at its foot. It rests at `REST` of the screen, with the moment's
  // district in view above it (home frames it, home-town.ts `focusMoment`), and a tap or a drag on its handle takes it
  // up to nearly full height to read, and back down.

  /** Taken up to nearly full height to read. */
  @state() private tall = false;
  /** Height (CSS px) while the handle is being dragged; null otherwise. */
  @state() private dragH: number | null = null;
  private drag: { y: number; h: number; moved: boolean } | null = null;

  private onGrabDown = (e: PointerEvent) => {
    const panel = this.querySelector<HTMLElement>('.moment-panel');
    if (!panel || e.button > 0) return;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* a pointer already gone */ }
    this.drag = { y: e.clientY, h: panel.getBoundingClientRect().height, moved: false };
  };

  private onGrabMove = (e: PointerEvent) => {
    const d = this.drag;
    if (!d) return;
    const dy = d.y - e.clientY;
    if (!d.moved && Math.abs(dy) < 6) return;
    d.moved = true;
    this.dragH = Math.max(innerHeight * 0.3, Math.min(innerHeight - 12, d.h + dy));
  };

  private onGrabUp = () => {
    const d = this.drag;
    this.drag = null;
    if (!d) return;
    if (!d.moved) { this.tall = !this.tall; return; }
    const h = this.dragH ?? d.h;
    this.dragH = null;
    // Dragged well below its resting height: it closes, as its ✕ does.
    if (h < innerHeight * REST * 0.7) { this.close(); return; }
    this.tall = h > innerHeight * (REST + 0.9) / 2;
  };

  private onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && !document.documentElement.classList.contains('capture-open')) this.close();
  };

  /** Close, Back and Escape: home takes the panel down (it listens for this, and for the route). */
  private close = () => {
    this.dispatchEvent(new CustomEvent('moment-close', { bubbles: true, composed: true }));
  };

  /** Something the town draws changed here (a seed kept, a word removed): home reads the town again as it closes. */
  private changed() {
    this.dispatchEvent(new CustomEvent('moment-changed', { bubbles: true, composed: true }));
  }

  /** The replies at the foot, one row each, side by side when there are several (a row per swipe). */
  private seedsFoot(m: Moment) {
    const rows: { r: { ca: string; en: string; when: string; latin?: string }; s?: MomentSeed }[] = this.seeds
      ? this.seeds.map((s) => ({ r: s, s }))
      : (m.analysis.suggested_replies ?? []).map((r) => ({ r }));
    if (!rows.length) return nothing;
    return html`<div class="card seeds mp-seeds">
      <div class="mp-seeds-q"><span>${t('moment.replies')}</span>${rows.length > 1 ? html`<span class="muted">${num(rows.length)}</span>` : nothing}</div>
      <div class="mp-seeds-row ${rows.length > 1 ? 'many' : ''}">
        ${rows.map(({ r, s }) => html`
          <div class="expr seed" data-seed=${s?.key ?? ''} data-state=${s?.state ?? ''} title=${r.when}>
            <div class="txt">
              <div class="mp-seed-ca">${this.listen(r.ca, 'inline')} ${this.targetLine(r)}</div>
              <div class="muted small" dir=${uiDir(r.en)}>${iso(r.en)}</div>
              ${s?.state === 'kept' && s.opens ? html`<div class="muted small opens"><span class="opens-reason">${opensLine(s.opens)}</span>${roundRecovery(this.readiness, this.readinessChecked, this.readinessFailed)}</div>` : nothing}
            </div>
            ${s ? this.seedControl(s) : nothing}
          </div>
        `)}
      </div>
      ${this.seedMsg ? html`<div class="note save-error" role="alert" style="margin-top:6px">${this.seedMsg}</div>` : nothing}
    </div>`;
  }

  render() {
    const m = this.moment;
    const h = this.dragH !== null ? `${Math.round(this.dragH)}px` : '';
    const title = m?.analysis.title ?? '';
    return html`
      <div class="moment-dim" aria-hidden="true" @click=${this.close}></div>
      <section class="moment-panel ${this.tall ? 'tall' : ''} ${this.dragH !== null ? 'dragging' : ''}" style=${h ? `height:${h}` : ''}
        role="dialog" aria-modal="true" aria-labelledby="moment-title">
        <button class="mp-grab" aria-label=${t(this.tall ? 'moment.panelDown' : 'moment.panelUp')} aria-expanded=${this.tall ? 'true' : 'false'}
          @pointerdown=${this.onGrabDown} @pointermove=${this.onGrabMove} @pointerup=${this.onGrabUp} @pointercancel=${this.onGrabUp}
          @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.tall = !this.tall; } }}></button>
        <div class="mp-head topbar">
          <h1 id="moment-title" dir=${uiDir(title)}>${m ? iso(title) : nothing}</h1>
          <button class="btn ghost icon mp-close" @click=${this.close} aria-label=${t('tend.close')}>✕</button>
        </div>
        ${this.error
          ? html`<div class="mp-scroll"><div class="card">${this.error}</div></div>`
          : !m
            ? html`<div class="mp-scroll"><div class="empty"><span class="spinner"></span></div></div>`
            : this.renderBody(m)}
      </section>`;
  }

  private renderBody(m: Moment) {
    const a = m.analysis;
    // A typed moment's line is what the learner wanted to say (ticket 36).
    const typed = m.source === 'text';
    // A new typed moment is an imagined scene: the learner's lines between another person's.
    const scene = typed && !!a.scene?.other && !a.scene.dropped && a.transcript.length > 0;
    const mine = isYou;
    const known = new Set(this.knownLines());
    const focus = this.items.length ? this.items.slice(0, 5) : [];
    const mission = m.mission ?? a.mission ?? '';
    return html`
      <div class="mp-scroll">
        ${this.deleteMsg
          ? html`<div class="note save-error" role="alert" style="margin-bottom:8px;display:flex;gap:8px;align-items:center">
              <span style="flex:1">${this.deleteMsg}</span>
              ${this.deleteRetry
                ? html`<button class="btn small" @click=${this.removeMoment} ?disabled=${this.deleting}>${t('common.retry')}</button>`
                : html`<button class="btn small" @click=${() => (this.deleteMsg = '')}>${t('common.dismiss')}</button>`}
            </div>`
          : nothing}

        <p class="mp-sit" dir=${uiDir(a.situation)}>${m.shared ? html`<span class="pill shared-pill">${t('shared.pill')}</span> ` : nothing}${m.newcomers ? html`<span class="pill newcomers-pill">${t('newcomers.pill')}</span> <span class="pill imagined-pill">${t('land.imagined')}</span> ` : scene ? html`<span class="pill imagined-pill">${t('moment.imaginedPill')}</span> ` : m.source === 'text' ? html`<span class="pill">${t('moment.textPill')}</span> ` : nothing}${iso(a.situation)}</p>

        <div class="mp-lines">
          <div class="mp-lines-head">
            <h3>${t(scene ? 'moment.imaginedScene' : typed ? 'moment.whatYouWanted' : 'moment.whatTheySaid')}</h3>
            ${a.transcript.length
              ? html`<button class="btn ghost small" style="min-height:28px;padding:2px 10px;text-transform:none" @click=${() => (this.allRevealed = !this.allRevealed)}>
                  ${this.allRevealed ? t('moment.hideTranslations') : t('moment.showAll')}
                </button>`
              : nothing}
          </div>
          ${scene
            ? html`<div class="scene-head">
                <div class="muted small">${t(m.newcomers ? 'newcomers.sceneNote' : 'moment.imaginedNote')}</div>
                <button class="btn small scene-play ${this.sceneOn ? 'on' : ''} ${this.scenePreparing ? 'preparing' : ''}" aria-busy=${this.scenePreparing ? 'true' : 'false'} @click=${() => this.playTheScene()}>
                  ${this.sceneOn ? `⏹ ${t('moment.stopListen')}` : `▶ ${t('moment.playScene')}`}
                </button>
                ${this.turnGate().open
                  ? html`<button class="btn small scene-your-turn ${this.turnRunning() ? 'on' : ''}" @click=${() => this.startTurn()}>
                      ${this.turnRunning() ? `⏹ ${t('moment.stopListen')}` : `▶ ${t('moment.yourTurn')}`}
                    </button>`
                  : nothing}
                ${m.answered
                  ? html`<a class="small nc-answered" href="/moment/${m.answered.momentId}" @click=${(e: Event) => { e.preventDefault(); go(`/moment/${m.answered!.momentId}`); }}>${personOf(m) ? tPart('newcomers.answeredLink', 'name', nameHtml(personOf(m)!)) : t('newcomers.answeredLinkPlain')}</a>`
                  : questionOf(m)
                    ? html`<button class="btn small primary newcomers-answer" @click=${() => openAnswer(m.id)}>${personOf(m) ? answerLabel(personOf(m)!) : t('newcomers.answer')}</button>`
                    : nothing}
                <span class="muted small scene-preparing" aria-live="polite">${this.scenePreparing ? t('moment.preparingScene') : ''}</span>
                ${this.renderTurnLock()}
              </div>
              ${this.renderTurn()}`
            : nothing}
          ${a.transcript.length ? this.renderKnown() : nothing}
          ${a.transcript.length
            ? html`<div class="transcript">
                ${a.transcript.map(
                  (line, i) => html`
                    <div class="line blueprint ${scene && i === 0 && this.scenePreparing ? 'wait-glow' : ''} ${scene && mine(line.speaker) ? 'line-you' : ''} ${known.has(i) ? 'known' : ''} ${known.has(i) && this.fresh.has(i) ? 'known-new' : ''}" @click=${() => this.toggle(i)}>
                      <span class="spk" title=${scene && !mine(line.speaker) ? line.speaker : nothing}><bdi>${typed && (!scene || mine(line.speaker)) ? t('moment.speakerYou') : scene ? [...line.speaker.trim()][0] ?? '' : line.speaker}</bdi>${known.has(i)
                        ? html`<span class="known-mark" role="img" aria-label=${t(this.fresh.has(i) ? 'known.markNew' : 'known.mark')} title=${t(this.fresh.has(i) ? 'known.markNew' : 'known.mark')}>✓</span>`
                        : nothing}</span>
                      <span>
                        ${scene && !mine(line.speaker) ? html`<span class="scene-who"><bdi dir="auto">${line.speaker}</bdi></span>` : nothing}
                        ${this.blueprintLine(i, line)}
                        <span class="en ${this.allRevealed || this.revealed.has(i) ? '' : 'hidden'}" dir=${uiDir(line.en)}>${iso(line.en)}</span>
                        ${this.blueprintMeanings(i, line, this.allRevealed || this.revealed.has(i))}
                      </span>
                      ${this.listen(line.ca, '', i)}
                    </div>
                  `,
                )}
              </div>
              <div class="muted small" style="margin-top:8px">${t('moment.tapLine')}</div>
              ${this.blueprints && Object.keys(this.blueprints.words).length
                ? html`<div class="muted small" style="margin-top:4px">${t('moment.blueprintsHint')}</div>`
                : nothing}`
            : html`<div class="muted small">${t('moment.noRecording')}</div>`}
        </div>

        <div class="card">
          <h2>${t('moment.whatGoingOn')}</h2>
          ${m.note ? html`<div class="muted small" dir="auto">${t('moment.yourNote', { note: m.note })}</div>` : nothing}
          ${a.language_notes ? html`<div class="muted small" style="margin-top:8px" dir=${uiDir(a.language_notes)}>${iso(a.language_notes)}</div>` : nothing}
          <div style="margin-top:10px;display:flex;gap:6px;flex-wrap:wrap">
            <span class="pill">${t('moment.confidence', { n: Math.round((a.confidence ?? 0) * 100) })}</span>
            ${m.analysisUsage || m.liveUsage?.length ? html`<span class="pill" title="analysis $${(m.analysisUsage?.costUsd ?? 0).toFixed(4)}; ${m.liveUsage?.length ?? 0} tutor session(s) $${(m.liveUsage ?? []).reduce((s, u) => s + u.costUsd, 0).toFixed(3)}">${t('moment.soFar', { usd: num(((m.analysisUsage?.costUsd ?? 0) + (m.liveUsage ?? []).reduce((s, u) => s + u.costUsd, 0)).toFixed(3)) })}</span>` : nothing}
            ${m.mock ? html`<span class="pill">${t('moment.sample')}</span>` : nothing}
          </div>
          <div class="muted small confidence-hint" style="margin-top:4px">${t(typed ? 'moment.confidenceHintTyped' : 'moment.confidenceHint')}</div>
          ${this.recording(m)}
        </div>

        ${focus.length
          ? html`<div class="card">
              <h2>${t('moment.learnFirst')}</h2>
              <div class="muted small" style="margin-bottom:8px">${t('moment.learnFirstHint')}</div>
              ${focus.map(
                (f) => html`
                  <div class="expr">
                    <div>${this.listen(f.ca, 'inline')} ${this.targetLine(f)} <span class="muted" dir=${uiDir(f.en)}>— ${iso(f.en)}</span>
                      ${f.forged ? html`<span class="pill">${t('moment.forged')}</span>` : nothing}</div>
                    ${this.triageButtons(f.ca)}
                  </div>
                `,
              )}
            </div>`
          : nothing}

        <div class="card">
          <h2>${t('moment.words')}</h2>
          <div class="vocab">
            ${a.vocabulary.map(
              (v, i) => html`<button class="chip ca ${this.openVocab === i ? 'open' : ''}" @click=${() => (this.openVocab = this.openVocab === i ? null : i)} dir="auto"><bdi dir="auto">${v.latin ?? v.ca}</bdi>${v.rude ? html` <span class="rude-tag">${t('rude.tag')}</span>` : nothing}</button>`,
            )}
          </div>
          ${this.openVocab !== null && a.vocabulary[this.openVocab]
            ? html`<div class="vocab-detail">
                <div>${this.listen(a.vocabulary[this.openVocab].ca, 'inline')} ${this.targetLine(a.vocabulary[this.openVocab])} <span dir=${uiDir(a.vocabulary[this.openVocab].en)}>— ${iso(a.vocabulary[this.openVocab].en)}</span> ${rudeTag(a.vocabulary[this.openVocab].rude)}</div>
                ${a.vocabulary[this.openVocab].note ? html`<div class="muted small" dir=${uiDir(a.vocabulary[this.openVocab].note)}>${iso(a.vocabulary[this.openVocab].note)}</div>` : nothing}
                ${a.vocabulary[this.openVocab].example ? html`<div class="small ca" style="margin-top:6px" dir="auto">${this.listen(a.vocabulary[this.openVocab].example!, 'inline')} ${this.targetLine({ ca: `“${a.vocabulary[this.openVocab].example}”`, latin: a.vocabulary[this.openVocab].example_latin && `“${a.vocabulary[this.openVocab].example_latin}”` })}</div>` : nothing}
                ${this.triageButtons(a.vocabulary[this.openVocab].ca)}
              </div>`
            : nothing}
        </div>

        ${a.expressions.length
          ? html`<div class="card expressions">
              <h2>${t('moment.expressions')}</h2>
              ${a.expressions.map(
                (e) => html`
                  <div class="expr">
                    <div>${this.listen(e.ca, 'inline')} ${this.targetLine(e)} <span class="muted" dir=${uiDir(e.en)}>— ${iso(e.en)}</span> ${rudeTag(e.rude)}</div>
                    ${e.literal ? html`<div class="muted small" dir="auto">${t('moment.literally', { text: e.literal })}</div>` : nothing}
                    <div class="small" style="margin-top:4px" dir=${uiDir(e.usage)}>${iso(e.usage)}</div>
                    ${this.opensNote(e.ca)}
                    ${this.triageButtons(e.ca)}
                  </div>
                `,
              )}
            </div>`
          : nothing}

        ${mission
          ? html`<div class="card">
              <h2>${t('moment.mission')}</h2>
              <div dir=${uiDir(mission)}>${iso(mission)}</div>
              <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:10px">
                <button class="btn ${this.rTried ? 'primary' : ''} small" style="min-height:30px;text-transform:none"
                  @click=${() => (this.rTried = !this.rTried)}>${t('moment.tried')}</button>
                <button class="btn ${this.rUnderstood ? 'primary' : ''} small" style="min-height:30px;text-transform:none"
                  @click=${() => (this.rUnderstood = !this.rUnderstood)}>${t('moment.understood')}</button>
                <button class="btn ${this.rStuck ? 'primary' : ''} small" style="min-height:30px;text-transform:none"
                  @click=${() => (this.rStuck = !this.rStuck)}>${t('moment.stuck')}</button>
              </div>
              <textarea class="note" style="margin-top:8px" placeholder=${t('moment.reflectionPlaceholder')}
                .value=${this.rNote} @input=${(e: Event) => (this.rNote = (e.target as HTMLTextAreaElement).value)}></textarea>
              <div style="display:flex;gap:8px;margin-top:8px">
                <button class="btn primary" style="flex:1" ?disabled=${this.saving} @click=${this.saveReflection}>${t('moment.saveReflection')}</button>
                ${this.rStuck
                  ? html`<button class="btn" style="flex:1" @click=${this.useStuckForNext}>${t('moment.startNext')}</button>`
                  : nothing}
              </div>
              ${this.missionMsg ? html`<div class="muted small" style="margin-top:6px">${this.missionMsg}</div>` : nothing}
              <div class="muted small" style="margin-top:6px">${t('moment.reflectionPrivate')}</div>
            </div>`
          : nothing}

        ${this.beta ? this.shareCard() : nothing}

        ${a.unclear?.length
          ? html`<div class="card"><h2>${t('moment.unclear')}</h2>${a.unclear.map((u) => html`<div class="muted small" dir=${uiDir(u)}>· ${iso(u)}</div>`)}</div>`
          : nothing}

        <button class="btn ghost small moment-report" @click=${() => openReport({ momentId: m.id, share: this.beta })}>${t('report.menu')}</button>
        <button class="btn ghost icon danger moment-delete mp-delete" @click=${this.deleteMoment} ?disabled=${this.deleting} aria-label=${t('moment.delete')}>
          <span aria-hidden="true">🗑</span> ${t('moment.delete')}
        </button>
      </div>
      <div class="mp-foot">
        ${this.seedsFoot(m)}
        <button class="btn primary block mp-main" @click=${() => go(`/practice/${m.id}`)}>${t('moment.practice')}</button>
      </div>
    `;
  }

  private toggle(i: number) {
    const s = new Set(this.revealed);
    s.has(i) ? s.delete(i) : s.add(i);
    this.revealed = s;
  }

  private deleteMoment = async () => {
    if (!this.moment || this.deleting) return;
    if (!confirm(t('moment.deleteConfirm'))) return;
    await this.removeMoment();
  };

  /**
   * Also the Retry after a failure (already confirmed). Gone already (404/410)
   * counts as done; any other refusal offers no Retry, only a Dismiss.
   */
  private removeMoment = async () => {
    if (!this.moment || this.deleting) return;
    this.deleting = true;
    this.deleteMsg = '';
    try {
      await api.deleteMoment(this.moment.id);
    } catch (e) {
      if (!(e instanceof HttpError && (e.status === 404 || e.status === 410))) {
        // A known server reason says itself; otherwise a refusal, or most likely the connection.
        this.deleteRetry = isRetryable(e);
        this.deleteMsg = e instanceof HttpError && e.code
          ? e.message
          : t(this.deleteRetry ? 'moment.deleteFailed' : 'moment.deleteRefused');
        this.deleting = false;
        return;
      }
    }
    this.dispatchEvent(new CustomEvent('moment-deleted', { bubbles: true, composed: true }));
    go('/', true);
  };
}

/** "22 Sept 2026" — a share's start date, readable without a clock. */
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(currentLang(), { day: 'numeric', month: 'short', year: 'numeric' });
}
